import type { OfflineLedger, OfflineLedgerEntry } from './offline-queue'
import {
  createOfflineLedger,
  isOwnedOfflineEnvelope,
  OfflineWriteIntentConflictError,
} from './offline-queue'

export interface OfflineStorage {
  /** Optional synchronous admission guard for stores that must restore first. */
  assertReady?(key: string): void
  getItem(key: string): Promise<string | null>
  setItem(key: string, value: string): Promise<void>
  removeItem(key: string): Promise<void>
}

export class OfflineLedgerStorageError extends Error {
  constructor() {
    super(
      'Saved changes could not be restored safely. Their original data has been kept.',
    )
    this.name = 'OfflineLedgerStorageError'
  }
}

export class OfflineLedgerOwnerRemovedError extends Error {
  constructor() {
    super(
      'This saved change belongs to a removed account. Sign in again and start a new change.',
    )
    this.name = 'OfflineLedgerOwnerRemovedError'
  }
}

const statuses = new Set([
  'queued',
  'inflight',
  'succeeded',
  'failed',
  'conflicted',
  'needs-review',
  'expired',
])

function isStoredEntry(
  value: unknown,
  ownerId: string,
): value is OfflineLedgerEntry {
  if (!value || typeof value !== 'object') return false
  const entry = value as Partial<OfflineLedgerEntry>
  return (
    isOwnedOfflineEnvelope(entry.envelope) &&
    entry.envelope.ownerId === ownerId &&
    typeof entry.envelope.label === 'string' &&
    typeof entry.status === 'string' &&
    statuses.has(entry.status) &&
    typeof entry.attempts === 'number' &&
    Number.isSafeInteger(entry.attempts) &&
    entry.attempts >= 0 &&
    typeof entry.updatedAt === 'number' &&
    Number.isFinite(entry.updatedAt) &&
    entry.updatedAt >= 0 &&
    (entry.completionOnly === undefined ||
      (entry.status === 'succeeded' && entry.completionOnly === true)) &&
    (entry.message === undefined || typeof entry.message === 'string')
  )
}

/** Validate the entire snapshot before publishing or merging any owned rows. */
export function parseOwnedOfflineSnapshot(
  raw: string | null,
  ownerId: string,
): OfflineLedgerEntry[] {
  if (raw === null) return []
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    throw new OfflineLedgerStorageError()
  }
  if (
    !Array.isArray(parsed) ||
    !parsed.every(entry => isStoredEntry(entry, ownerId))
  )
    throw new OfflineLedgerStorageError()
  if (
    new Set(parsed.map(entry => entry.envelope.idempotencyKey)).size !==
    parsed.length
  )
    throw new OfflineWriteIntentConflictError()
  return parsed
}

/** A user's outbox survives sign-out; it is only restored for its original owner. */
export function createOwnedOfflineLedger(
  storage: OfflineStorage,
  legacyKey: string,
) {
  const knownOwners = new Set<string>()
  const savedValues = new Map<string, string | null>()
  const failures = new Map<string, unknown>()
  const unreadableOwners = new Set<string>()
  let writes = Promise.resolve()
  const ownerEpochs = new Map<string, number>()
  const blockedOwners = new Set<string>()
  const seenOperations = new Map<string, Set<string>>()
  const retiredOperations = new Map<string, Set<string>>()
  const removals = new Map<string, Promise<void>>()
  const epoch = (owner: string) => ownerEpochs.get(owner) ?? 0
  const admit = (entry: OfflineLedgerEntry) => {
    if (!isOwnedOfflineEnvelope(entry.envelope)) return
    const { ownerId, idempotencyKey } = entry.envelope
    storage.assertReady?.(keyFor(ownerId))
    if (
      blockedOwners.has(ownerId) ||
      retiredOperations.get(ownerId)?.has(idempotencyKey)
    )
      throw new OfflineLedgerOwnerRemovedError()
  }
  const remember = (entry: OfflineLedgerEntry) => {
    if (!isOwnedOfflineEnvelope(entry.envelope)) return
    const { ownerId, idempotencyKey } = entry.envelope
    let seen = seenOperations.get(ownerId)
    if (!seen) {
      seen = new Set()
      seenOperations.set(ownerId, seen)
    }
    seen.add(idempotencyKey)
  }
  const keyFor = (ownerId: string) =>
    `${legacyKey}:v2:${encodeURIComponent(ownerId)}`
  const memory = createOfflineLedger(entries => {
    const groups = new Map<string, OfflineLedgerEntry[]>()
    for (const entry of entries) {
      if (!isOwnedOfflineEnvelope(entry.envelope)) continue
      const owner = entry.envelope.ownerId
      knownOwners.add(owner)
      const group = groups.get(owner)
      if (group) group.push(entry)
      else groups.set(owner, [entry])
    }
    const snapshots = [...knownOwners].map(
      owner =>
        [owner, keyFor(owner), groups.get(owner) ?? [], epoch(owner)] as const,
    )
    writes = writes
      .catch(() => undefined)
      .then(async () => {
        const errors: unknown[] = []
        for (const [owner, key, entries, generation] of snapshots) {
          if (blockedOwners.has(owner) || epoch(owner) !== generation) continue
          try {
            if (unreadableOwners.has(owner))
              throw new OfflineLedgerStorageError()
            const value = entries.length
              ? JSON.stringify(
                  entries.map(entry => ({
                    envelope: entry.envelope,
                    status: entry.status,
                    attempts: entry.attempts,
                    updatedAt: entry.updatedAt,
                    message: entry.message,
                    ...(entry.status === 'succeeded'
                      ? { completionOnly: true }
                      : {}),
                  })),
                )
              : null
            if (savedValues.has(owner) && savedValues.get(owner) === value) {
              failures.delete(owner)
              continue
            }
            if (value === null) await storage.removeItem(key)
            else await storage.setItem(key, value)
            if (blockedOwners.has(owner) || epoch(owner) !== generation)
              continue
            savedValues.set(owner, value)
            failures.delete(owner)
          } catch (error) {
            if (blockedOwners.has(owner) || epoch(owner) !== generation)
              continue
            failures.set(owner, error)
            errors.push(error)
          }
        }
        if (errors.length) throw errors[0]
      })
    // Preserve rejection for flush(); background writes must not become unhandled.
    void writes.catch(() =>
      console.warn('[OfflineQueue] Persistence could not be confirmed'),
    )
  })

  const ledger: OfflineLedger = {
    ...memory,
    upsert(entry) {
      admit(entry)
      remember(entry)
      memory.upsert(entry)
    },
    hydrate(entries) {
      entries.forEach(admit)
      entries.forEach(remember)
      memory.hydrate(entries)
    },
  }

  /** Retire memory after privileged transactional storage cleanup. Never delete
   * storage here: a delayed acknowledgement may belong to a later login. */
  const retireOwner = (ownerId: string): void => {
    if (!ownerId) throw new OfflineLedgerOwnerRemovedError()
    ownerEpochs.set(ownerId, epoch(ownerId) + 1)
    blockedOwners.add(ownerId)
    const retired = retiredOperations.get(ownerId) ?? new Set<string>()
    for (const id of seenOperations.get(ownerId) ?? []) retired.add(id)
    retiredOperations.set(ownerId, retired)
    seenOperations.delete(ownerId)
    savedValues.delete(ownerId)
    failures.delete(ownerId)
    knownOwners.delete(ownerId)
    unreadableOwners.delete(ownerId)
    for (const entry of memory.list())
      if (entry.envelope.ownerId === ownerId)
        memory.remove(entry.envelope.idempotencyKey, ownerId)
    knownOwners.delete(ownerId)
  }

  return {
    ledger,
    retireOwner,
    async flush(ownerId?: string | null): Promise<void> {
      try {
        await writes
      } catch (error) {
        if (!ownerId) throw error
      }
      if (ownerId && failures.has(ownerId)) throw failures.get(ownerId)
    },
    /** Caller must retire the account session first. All old snapshots and
     * responses remain fenced even if this owner signs in again in this process. */
    async forgetOwner(ownerId: string): Promise<void> {
      retireOwner(ownerId)
      const removal = writes
        .catch(() => undefined)
        .then(async () => {
          await storage.removeItem(keyFor(ownerId))
          savedValues.delete(ownerId)
          failures.delete(ownerId)
          knownOwners.delete(ownerId)
          unreadableOwners.delete(ownerId)
        })
      writes = removal
      removals.set(ownerId, removal)
      // Retain failed removal evidence until an explicit retry succeeds.
      void removal.catch(() => undefined)
      await removal
    },
    async restore(ownerId: string | null): Promise<void> {
      const generation = ownerId ? epoch(ownerId) : 0
      const assertCurrent = () => {
        if (ownerId && epoch(ownerId) !== generation)
          throw new OfflineLedgerOwnerRemovedError()
      }
      // Old work cannot be attributed safely. Keep its original bytes for
      // recovery, but never expose or automatically replay them under a login.
      const legacy = await storage.getItem(legacyKey)
      if (legacy !== null) {
        await storage.setItem(`${legacyKey}:quarantine-v1`, legacy)
        await storage.removeItem(legacyKey)
      }
      if (!ownerId) return
      await removals.get(ownerId)
      await writes.catch(() => undefined)
      assertCurrent()
      const raw = await storage.getItem(keyFor(ownerId))
      assertCurrent()
      blockedOwners.delete(ownerId)
      if (raw === null) return
      // A partial restore followed by persistence would erase rejected rows.
      // Validate the entire owner snapshot and retain its bytes on any failure.
      try {
        const parsed = parseOwnedOfflineSnapshot(raw, ownerId)
        ledger.hydrate(parsed)
      } catch (error) {
        unreadableOwners.add(ownerId)
        throw error instanceof SyntaxError
          ? new OfflineLedgerStorageError()
          : error
      }
      unreadableOwners.delete(ownerId)
      knownOwners.add(ownerId)
      ledger.prune()
    },
  }
}
